import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Upload, Trash2, Building2, Landmark, Percent } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { billingApi } from '../../api';

const Field = ({ id, label, form, setForm, type = 'text', ...rest }) => (
  <div>
    <Label htmlFor={`bs-${id}`}>{label}</Label>
    <Input id={`bs-${id}`} type={type} value={form[id] ?? ''} onChange={(e) => setForm({ ...form, [id]: type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value })} className="mt-1.5" data-testid={`billing-settings-${id}`} {...rest}/>
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
    <div className="px-5 py-4 border-b border-stone-100 flex items-center gap-2 font-semibold text-stone-900"><Icon size={16} className="text-emerald-800"/> {title}</div>
    <div className="p-5 grid sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

export const BillingSettings = ({ toast, isOwner }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logoKey, setLogoKey] = useState(Date.now());
  const fileRef = useRef(null);
  useEffect(() => { billingApi.settings().then(setForm); }, []);
  if (!form) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div>;

  const fail = (t) => (e) => toast({ title: t, description: e?.response?.data?.detail || 'Try again.', variant: 'destructive' });
  const save = (e) => {
    e.preventDefault();
    setBusy(true);
    const { hasLogo, ...body } = form;
    billingApi.saveSettings(body).then((s) => { setForm(s); toast({ title: 'Invoice settings saved' }); }).catch(fail('Save failed')).finally(() => setBusy(false));
  };
  const upload = (file) => billingApi.uploadLogo(file).then(() => { setForm({ ...form, hasLogo: true }); setLogoKey(Date.now()); toast({ title: 'Logo uploaded' }); }).catch(fail('Upload failed'));
  const p = { form, setForm, disabled: !isOwner };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="billing-settings-form">
      {!isOwner && <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">Only the firm owner can change invoice settings.</div>}
      <Section icon={Building2} title="Business details on invoices">
        <div className="sm:col-span-2 flex items-center gap-4">
          <div className="h-16 w-40 rounded-lg border border-dashed border-stone-300 grid place-items-center bg-stone-50 overflow-hidden" data-testid="billing-logo-preview">
            {form.hasLogo ? <img key={logoKey} src={billingApi.logoUrl()} alt="Firm logo" className="max-h-14 max-w-[150px] object-contain"/> : <span className="text-xs text-stone-400">No logo</span>}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ''; }}/>
          {isOwner && <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="billing-logo-upload-btn"><Upload size={14} className="mr-1.5"/> {form.hasLogo ? 'Replace logo' : 'Upload logo'}</Button>}
          {isOwner && form.hasLogo && <Button type="button" variant="ghost" size="sm" onClick={() => billingApi.removeLogo().then(() => setForm({ ...form, hasLogo: false }))} className="text-red-700" data-testid="billing-logo-remove-btn"><Trash2 size={14}/></Button>}
          <span className="text-xs text-stone-500">PNG or JPG, under 2 MB</span>
        </div>
        <Field id="businessName" label="Business name" {...p}/>
        <Field id="registrationNumber" label="Company registration no." {...p}/>
        <Field id="email" label="Billing email" {...p}/>
        <Field id="phone" label="Phone" {...p}/>
        <div className="sm:col-span-2"><Label htmlFor="bs-address">Address</Label><Textarea id="bs-address" disabled={!isOwner} rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" data-testid="billing-settings-address"/></div>
      </Section>
      <Section icon={Percent} title="VAT, rates & numbering">
        <Field id="vatNumber" label="Your VAT number (shows TAX INVOICE)" {...p}/>
        <Field id="vatRate" label="VAT rate (%)" type="number" step="0.01" min="0" max="100" {...p}/>
        <Field id="defaultRate" label="Default hourly rate (R) — used when a staff member has no rate" type="number" min="0" {...p}/>
        <Field id="paymentTermsDays" label="Payment terms (days)" type="number" min="0" max="365" {...p}/>
        <Field id="invoicePrefix" label="Invoice number prefix" maxLength={10} {...p}/>
      </Section>
      <Section icon={Landmark} title="Banking details (printed on every invoice)">
        <Field id="bankName" label="Bank" {...p}/>
        <Field id="accountName" label="Account holder" {...p}/>
        <Field id="accountNumber" label="Account number" {...p}/>
        <Field id="branchCode" label="Branch code" {...p}/>
        <Field id="accountType" label="Account type" placeholder="Cheque / Current" {...p}/>
        <Field id="footerNote" label="Footer note" {...p}/>
      </Section>
      {isOwner && <div className="flex justify-end"><Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="billing-settings-save-btn">{busy && <Loader2 size={14} className="mr-2 animate-spin"/>} Save settings</Button></div>}
    </form>
  );
};
